import { useState } from 'react';
import { Percent, Plus } from 'lucide-react';
import { useTaxOptions } from '@repo/queries';
import type { TaxOption } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { DataTable } from '@/features/table/DataTable';
import { describeAppError } from '@/features/common/describeAppError';
import { Alert } from '@/components/ui/alert';
import { TaxOptionDialog } from '@/features/tax/TaxOptionDialog';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function TaxesListScreen() {
  const { canEditOrg } = useActiveRole();
  const { taxOptions, loading, error } = useTaxOptions();
  const [active, setActive] = useState<TaxOption | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }: { row: { original: TaxOption } }) => (
        <div>
          <div className="font-medium text-foreground">{row.original.name}</div>
          {row.original.description ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {row.original.description}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'rate',
      header: () => <span className="text-right">Rate</span>,
      cell: ({ row }: { row: { original: TaxOption } }) => (
        <span className="block text-right font-mono text-sm font-medium">
          {row.original.rate}%
        </span>
      ),
    },
    {
      accessorKey: 'kind',
      header: 'Kind',
      cell: ({ row }: { row: { original: TaxOption } }) => (
        <Badge variant={row.original.kind === 'add' ? 'default' : 'warning'}>
          {row.original.kind === 'add' ? 'Add' : 'Withhold'}
        </Badge>
      ),
    },
    {
      accessorKey: 'region',
      header: 'Region',
      cell: ({ row }: { row: { original: TaxOption } }) => row.original.region ?? '—',
    },
  ];

  return (
    <Page>
      <PageHeader
        title="Tax options"
        description="Per-business tax presets your invoices can apply."
        actions={
          canEditOrg ? (
            <Button
              onClick={() => {
                setActive(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="size-4" /> New tax option
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert variant="danger" className="mb-4">
          {describeAppError(error).message}
        </Alert>
      ) : null}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton key={idx} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={taxOptions ?? []}
          onRowClick={
            canEditOrg
              ? (row) => {
                  setActive(row);
                  setDialogOpen(true);
                }
              : undefined
          }
          emptyState={
            <EmptyState
              icon={<Percent className="size-7" />}
              title="No tax options yet"
              description="Add the rates you bill at to keep invoices consistent."
              actions={
                canEditOrg ? (
                  <Button
                    onClick={() => {
                      setActive(null);
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="size-4" /> New tax option
                  </Button>
                ) : null
              }
            />
          }
        />
      )}

      <TaxOptionDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setActive(null);
        }}
        taxOption={active}
      />
    </Page>
  );
}
